import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { RepositoryDetailSkeleton } from '../../components/ui/Skeleton';
import { PageContainer, ToolbarDivider } from '../../components/ui/PageLayout';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import RepositoryOverviewTab from './tabs/RepositoryOverviewTab';
import RepositoryMetricsTab from './tabs/RepositoryMetricsTab';
import RepositoryCommitsTab from './tabs/RepositoryCommitsTab';
import RepositoryHistoryTab from './tabs/RepositoryHistoryTab';
import RepositoryActivityTab from './tabs/RepositoryActivityTab';
import RepositoryPullRequestsTab from './tabs/RepositoryPullRequestsTab';
import RepositoryBranchesTab from './tabs/RepositoryBranchesTab';
import RepositoryContributorsTab from './tabs/RepositoryContributorsTab';
import { getRepoDeploymentBranches } from '../../utils/deploymentBranches';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import { useResolvedOrgId } from '../../hooks/useResolvedOrgId';
import { getRepositoryDetailVisibleTabs } from './repositoryDetailConstants';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import {
  formatCodeIndexStageLabel,
  formatCodeIndexStatusLabel,
} from '../../utils/codeIndexProgress';
import {
  repositoryBranchesColumns,
  repositoryCommitsColumns,
  repositoryActivityColumns,
  repositoryPullRequestsColumns,
} from './repositoryDetailColumns';
import RepositorySettingsModal from './RepositorySettingsModal';
import ProjectDocumentsPanel from '../ProjectDetail/ProjectDocumentsPanel';
import RepositoryDetailToolbar from './RepositoryDetailToolbar';
import { useRepositoryDetailQuery } from './useRepositoryDetailQuery';
import { useRepositoryTechStack } from './useRepositoryTechStack';
import { useRepositoryTabData } from './useRepositoryTabData';
import { useRepositoryActions } from './useRepositoryActions';
import { useRepositoryBranchSettings } from './useRepositoryBranchSettings';

const RepositoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [techCategoriesOpen, setTechCategoriesOpen] = useState(() => new Set());
  const [activeTab, setActiveTab] = useState('overview');
  const [commitsListFilters, setCommitsListFilters] = useState({
    branch: '',
    author: '',
    from: '',
    to: '',
  });
  const [contributorsTimeframe, setContributorsTimeframe] = useState('week');
  const [contributorsStatus, setContributorsStatus] = useState('active');
  const [deploymentPeriod, setDeploymentPeriod] = useState('yearly');
  const [leadTimePeriod, setLeadTimePeriod] = useState('yearly');
  const [doraChartType, setDoraChartType] = useState('area');
  const [leadTimeChartType, setLeadTimeChartType] = useState('area');
  const [churnPeriod, setChurnPeriod] = useState('yearly');
  const [churnChartType, setChurnChartType] = useState('area');

  const { orgId } = useResolvedOrgId();
  const { flags } = useFeatureFlags();
  const canCancelSync = flags['api.repositories.sync.cancel'] !== false;
  const canTriggerCodeIndex = flags['api.repositories.codeIndex.trigger'] !== false;
  const knowSourcesRead = flags['api.projects.knowledgeSources.read'] !== false;
  const knowSourcesWrite = flags['api.projects.knowledgeSources.write'] !== false;
  const orgName = useOrganisationName();
  const visibleTabs = getRepositoryDetailVisibleTabs(flags);

  const { repository, loading, repoQuery, codeIndexActive, codeIndexProgressPct } =
    useRepositoryDetailQuery(id);

  const historyBranch = useMemo(() => getRepoDeploymentBranches(repository)[0], [repository]);

  const { techStack, techStackLoading, techStackScanning, refetchTechStack, handleScanTechStack } =
    useRepositoryTechStack(id, repository?.id);

  const deploymentWindowDays =
    deploymentPeriod === 'weekly' ? 7 : deploymentPeriod === 'yearly' ? 365 : 30;
  const leadTimeWindowDays =
    leadTimePeriod === 'weekly' ? 7 : leadTimePeriod === 'yearly' ? 365 : 30;
  const churnWindowDays = churnPeriod === 'weekly' ? 7 : churnPeriod === 'yearly' ? 365 : 30;

  const tabData = useRepositoryTabData({
    id,
    orgId,
    activeTab,
    commitsListFilters,
    contributorsTimeframe,
    contributorsStatus,
    deploymentWindowDays,
    leadTimeWindowDays,
    churnWindowDays,
    historyBranch,
  });

  const actions = useRepositoryActions({
    id,
    repository,
    repoQuery,
    refetchTechStack,
    canCancelSync,
  });

  const branchSettings = useRepositoryBranchSettings({
    id,
    repository,
    onSync: actions.handleSync,
  });

  useEffect(() => {
    if (activeTab === 'documents' && flags['ui.repositoryDetail.documentsTab'] === false) {
      setActiveTab('overview');
    }
  }, [flags, activeTab]);

  useEffect(() => {
    setCommitsListFilters({ branch: '', author: '', from: '', to: '' });
  }, [id]);

  const repositoryBreadcrumbItems = useMemo(() => {
    const orgLabel = orgName || 'Organisation';
    if (!repository) return null;
    const p = repository.project;
    const s = p?.stream;
    if (p?.id && s?.id && (s.name || p.name)) {
      return [
        { label: orgLabel, to: '/' },
        { label: s.name || 'Stream', to: `/streams/${s.id}` },
        { label: p.name || 'Project', to: `/streams/${s.id}/projects/${p.id}` },
        { label: repository.fullName },
      ];
    }
    return [
      { label: orgLabel, to: '/' },
      { label: 'Repositories', to: '/repositories' },
      { label: repository.fullName },
    ];
  }, [repository, orgName]);

  if (loading) {
    return <RepositoryDetailSkeleton />;
  }

  if (!repository && !loading) {
    return (
      <PageContainer>
        <button
          type="button"
          onClick={() => navigate('/repositories')}
          className="btn-icon-mdl"
          aria-label="Back to Repositories"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <ToolbarDivider />
        <p className="mt-4 text-slate-400">Repository not found.</p>
      </PageContainer>
    );
  }

  const repoData = repository;

  return (
    <PageContainer>
      <RepositoryDetailToolbar
        repoData={repoData}
        breadcrumbItems={repositoryBreadcrumbItems}
        isSyncingNow={actions.isSyncingNow}
        canCancelSync={canCancelSync}
        onSync={actions.handleSync}
        onStopSync={actions.handleStopSync}
        onOpenSettings={branchSettings.openSettings}
        canTriggerCodeIndex={canTriggerCodeIndex}
        repository={repository}
        codeIndexTriggering={actions.codeIndexTriggering}
        onTriggerCodeIndex={actions.handleTriggerCodeIndex}
        deleting={actions.deleting}
        onDelete={actions.handleDelete}
      />
      {repoData.description && (
        <p className="mb-4 text-sm text-slate-400 -mt-1">{repoData.description}</p>
      )}

      {actions.syncStatus && (
        <div className="mb-4 p-4 bg-blue-900/20 border-blue-800 rounded-md">
          <p className="text-blue-300">{actions.syncStatus}</p>
        </div>
      )}

      {codeIndexActive ? (
        <div
          className="mb-4 rounded-md border-sky-800 bg-sky-900/20 p-4"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-wrap items-center gap-2 text-sky-100">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            <span className="font-medium">
              {formatCodeIndexStatusLabel(repository?.codeIndexStatus, repository?.codeIndexStage)}
            </span>
            <span className="text-sky-200">
              — {formatCodeIndexStageLabel(repository?.codeIndexStage)} · {codeIndexProgressPct}%
            </span>
          </div>
          <div
            className="mt-2 h-2 w-full max-w-xl overflow-hidden rounded-full bg-sky-900/50"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={codeIndexProgressPct}
            aria-label={`Code index progress ${codeIndexProgressPct}%`}
          >
            <div
              className="h-full rounded-full bg-sky-400 transition-[width] duration-300"
              style={{ width: `${codeIndexProgressPct}%` }}
            />
          </div>
        </div>
      ) : null}

      <div className="border-b border-dark-border mb-6">
        <nav className="flex gap-1 -mb-px" role="tablist" aria-label="Repository sections">
          {visibleTabs.map(({ id: tabId, label, icon: Icon }) => (
            <button
              key={tabId}
              type="button"
              role="tab"
              id={`repo-tab-${tabId}`}
              aria-selected={activeTab === tabId}
              aria-controls={`repo-tabpanel-${tabId}`}
              tabIndex={activeTab === tabId ? 0 : -1}
              onClick={() => setActiveTab(tabId)}
              className={animatedTabTriggerClass(activeTab === tabId)}
            >
              <AnimatedTabIndicator
                layoutId="repo-detail-tab-indicator"
                isActive={activeTab === tabId}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                <Icon className="w-4 h-4" aria-hidden />
                {label}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'documents' && (
        <div role="tabpanel" id="repo-tabpanel-documents" aria-labelledby="repo-tab-documents">
          {repository?.projectId && !repository.disconnectedAt ? (
            <ProjectDocumentsPanel
              repositoryKnowledgeApi
              repositoryEntityId={id}
              knowRead={knowSourcesRead}
              knowWrite={knowSourcesWrite}
              title="Repository documents"
              description="Files and links here are scoped to this repository. They are embedded with the project’s knowledge for briefings and for Vanti when you chat from this repository."
            />
          ) : (
            <div className="card-mdl-static p-6 text-sm text-slate-400">
              <p className="font-medium text-slate-100">Assign to a project first</p>
              <p className="mt-2">
                Connect this repository to a project from the project’s Repositories section, then
                return here to upload documents and links.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'overview' && (
        <RepositoryOverviewTab
          repositoryId={id}
          organisationId={orgId}
          repoData={repoData}
          deploymentDoraMeta={tabData.deploymentDoraMeta}
          leadTimeDoraMeta={tabData.leadTimeDoraMeta}
          churnData={tabData.churnData}
          onGoToMetrics={() => setActiveTab('metrics')}
          metricsFetchScope={orgId && id ? { orgId, repositoryId: id } : undefined}
          cycleTimeConfig={repository?.cycleTimeConfig}
          techStack={techStack}
          techStackLoading={techStackLoading}
          techStackScanning={techStackScanning}
          techCategoriesOpen={techCategoriesOpen}
          setTechCategoriesOpen={setTechCategoriesOpen}
          onScanTechStack={handleScanTechStack}
        />
      )}

      {activeTab === 'metrics' && (
        <RepositoryMetricsTab
          deploymentLoading={tabData.deploymentLoading}
          deploymentDora={tabData.deploymentDora}
          deploymentDoraMeta={tabData.deploymentDoraMeta}
          deploymentWindowDays={deploymentWindowDays}
          deploymentPeriod={deploymentPeriod}
          doraChartType={doraChartType}
          setDeploymentPeriod={setDeploymentPeriod}
          setDoraChartType={setDoraChartType}
          leadTimeLoading={tabData.leadTimeLoading}
          leadTimeDora={tabData.leadTimeDora}
          leadTimeDoraMeta={tabData.leadTimeDoraMeta}
          leadTimePeriod={leadTimePeriod}
          leadTimeChartType={leadTimeChartType}
          setLeadTimePeriod={setLeadTimePeriod}
          setLeadTimeChartType={setLeadTimeChartType}
          churnLoading={tabData.churnLoading}
          churnData={tabData.churnData}
          churnWindowDays={churnWindowDays}
          churnPeriod={churnPeriod}
          churnChartType={churnChartType}
          setChurnPeriod={setChurnPeriod}
          setChurnChartType={setChurnChartType}
          onDownloadDoraLabelsTemplate={actions.handleDownloadDoraLabelsTemplate}
          metricsFetchScope={orgId && id ? { orgId, repositoryId: id } : undefined}
          doraLabelsFilePresent={repoData.doraLabelsFilePresent}
        />
      )}

      {activeTab === 'commits' && (
        <RepositoryCommitsTab
          repositoryId={id}
          tabCommits={tabData.tabCommits}
          tabCommitsMeta={tabData.tabCommitsMeta}
          tabCommitsLoading={tabData.tabCommitsLoading}
          commitsColumns={repositoryCommitsColumns}
          commitsListFilters={commitsListFilters}
          onCommitsListFiltersChange={setCommitsListFilters}
          commitFilterBranches={tabData.commitFilterBranches}
          contributors={tabData.contributors}
          contributorsLoading={tabData.contributorsLoading}
        />
      )}

      {activeTab === 'history' && (
        <RepositoryHistoryTab
          repoData={repoData}
          historyCommits={tabData.historyCommits}
          historyLoading={tabData.historyLoading}
        />
      )}

      {activeTab === 'activity' && (
        <RepositoryActivityTab
          repositoryId={id}
          tabActivity={tabData.tabActivity}
          tabActivityLoading={tabData.tabActivityLoading}
          activityColumns={repositoryActivityColumns}
          activityTypeOptions={tabData.activityTypeOptions}
        />
      )}

      {activeTab === 'pull-requests' && (
        <RepositoryPullRequestsTab
          repositoryId={id}
          pullRequests={tabData.pullRequests}
          pullRequestsStats={tabData.pullRequestsStats}
          pullRequestsLoading={tabData.pullRequestsLoading}
          pullRequestsColumns={repositoryPullRequestsColumns}
        />
      )}

      {activeTab === 'branches' && (
        <RepositoryBranchesTab
          repositoryId={id}
          tabBranches={tabData.tabBranches}
          tabBranchesStats={tabData.tabBranchesStats}
          tabBranchesLoading={tabData.tabBranchesLoading}
          branchesColumns={repositoryBranchesColumns}
        />
      )}

      {activeTab === 'contributors' && (
        <RepositoryContributorsTab
          repositoryId={id}
          contributorRows={tabData.contributorRows}
          contributorsLoading={tabData.contributorsCardLoading}
          timeframe={contributorsTimeframe}
          status={contributorsStatus}
          onTimeframeChange={setContributorsTimeframe}
          onStatusChange={setContributorsStatus}
        />
      )}

      <RepositorySettingsModal
        isOpen={branchSettings.settingsOpen}
        onClose={() => branchSettings.setSettingsOpen(false)}
        savingBranch={branchSettings.savingBranch}
        branches={branchSettings.branches}
        branchesLoading={branchSettings.branchesLoading}
        selectedDeploymentBranches={branchSettings.selectedDeploymentBranches}
        onSelectedDeploymentBranchesChange={branchSettings.setSelectedDeploymentBranches}
        customDeploymentBranchesText={branchSettings.customDeploymentBranchesText}
        onCustomDeploymentBranchesTextChange={branchSettings.setCustomDeploymentBranchesText}
        settingsQaBranch={branchSettings.settingsQaBranch}
        onSettingsQaBranchChange={branchSettings.setSettingsQaBranch}
        settingsUatBranch={branchSettings.settingsUatBranch}
        onSettingsUatBranchChange={branchSettings.setSettingsUatBranch}
        cycleBotLoginsText={branchSettings.cycleBotLoginsText}
        onCycleBotLoginsTextChange={branchSettings.setCycleBotLoginsText}
        onCycleBotLoginsInteraction={branchSettings.handleCycleBotLoginsInteraction}
        onResetCycleBotDefaults={branchSettings.handleResetCycleBotDefaults}
        onSave={branchSettings.handleSaveBranches}
      />
    </PageContainer>
  );
};

export default RepositoryDetail;
